import React from 'react'
import { isRouteErrorResponse, useLocation, useNavigate, useRouteError } from 'react-router-dom'
import { Loading } from '@components/ui/Loading'
import { buildLoginErrorRedirectPath } from '@auth/components/LoginCallback/loginErrorRedirectPath'
import { routeLogger } from '@utils/routeLogger'

const GENERIC_LOGIN_ERROR = 'Authentication failed'
const BASE_RECOVERY_PATH = '/account'

/** Paths where a route render failure is treated as login/OAuth recovery (CHP2-14792). */
const OAUTH_CALLBACK_PATHS = new Set(['/account/login/callback', '/account/logout/callback'])

function isOAuthCallbackPathname(pathname: string): boolean {
  return OAUTH_CALLBACK_PATHS.has(pathname)
}

function recoveryTargetMatchesLocation(pathname: string, search: string, target: string): boolean {
  return `${pathname}${search}` === target
}

function formatRouteErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  if (isRouteErrorResponse(error)) return `${error.status} ${error.statusText}`
  return 'An unexpected error occurred'
}

const AppRouteErrorFallback = ({ error }: { error: unknown }) => {
  const message = formatRouteErrorMessage(error)

  return (
    <div
      className="flex flex-col items-center justify-center min-h-screen p-4"
      role="alert"
      data-testid="app-route-error-fallback">
      <div className="p-6 bg-white rounded shadow-lg max-w-lg">
        <h2 className="text-2xl font-bold text-red-600 mb-4">Something went wrong</h2>
        <p className="text-gray-700">{message}</p>
      </div>
    </div>
  )
}

/**
 * Root route error UI (CHP2-14792): avoids React Router's default "Unexpected Application Error"
 * for OAuth callback routes by redirecting to `/account` with `loginError`, consistent with
 * OAuth failure handling. Other route errors render a simple fallback instead of masking them
 * with a login redirect.
 */
export const AppRouteError: React.FC = () => {
  const error = useRouteError()
  const navigate = useNavigate()
  const location = useLocation()
  const handledRef = React.useRef(false)

  const recoveryTarget = buildLoginErrorRedirectPath(BASE_RECOVERY_PATH, GENERIC_LOGIN_ERROR)
  const onOAuthCallback = isOAuthCallbackPathname(location.pathname)
  const targetWouldLoop = recoveryTargetMatchesLocation(location.pathname, location.search, recoveryTarget)
  const shouldRedirectToLoginRecovery = onOAuthCallback && !targetWouldLoop

  React.useEffect(() => {
    if (handledRef.current) return
    handledRef.current = true

    if (isRouteErrorResponse(error)) {
      console.error('[AppRouteError]', error.status, error.statusText)
    } else {
      console.error('[AppRouteError]', error)
    }

    if (!shouldRedirectToLoginRecovery) return

    routeLogger.log({
      type: 'redirect',
      from: typeof window !== 'undefined' ? window.location.pathname : '',
      to: recoveryTarget,
      method: 'replace',
    })
    navigate(recoveryTarget, { replace: true })
  }, [error, navigate, recoveryTarget, shouldRedirectToLoginRecovery])

  if (!shouldRedirectToLoginRecovery) {
    return <AppRouteErrorFallback error={error} />
  }

  return (
    <Loading
      heading="Redirecting…"
      subheading="One moment while we take you back to sign in."
    />
  )
}
